import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, watch } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { useElementHover, useMousePressed } from '@vueuse/core';
import {
  useSwiperAutoplayProgress,
  useBreakpoint,
  useMediaAsset,
  useSwiperElement,
} from '~/composables';
import { hideAllPoppers } from 'floating-vue';
import '~/assets/css/floating-vue.css';
import { isTouchDevice } from '~/helpers';

interface Props {
  data?: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BannersSlider',
  props: {
    data: { default: () => ({}) }
  },
  setup(__props: any) {



const { getAssetUrl } = useMediaAsset();

const duration = 0.65;

const animations = {
  slideUp: [
    { opacity: 0, yPercent: 50, ease: 'circ' },
    { opacity: 1, yPercent: 0, ease: 'circ', duration },
  ],
  fadeIn: [
    { opacity: 0, ease: 'linear' },
    { opacity: 1, ease: 'linear', duration: 1.5 },
  ],
  fadeInWithBlur: [
    { opacity: 0, backdropFilter: 'blur(0px)', ease: 'linear' },
    { opacity: 1, backdropFilter: 'blur(10px)', ease: 'linear', duration: 1.5 },
  ],
};

const runSlideStartAnimation = ($gsap: GSAP, rootEl: HTMLElement) => {
  const { fadeIn, fadeInWithBlur, slideUp } = animations;
  const activeSlide = rootEl.querySelector('.swiper-slide-active');

  if (!activeSlide) return;

  const markersCanvas = activeSlide!.querySelector('.markers');
  let delay = 0;

  if (markersCanvas) {
    delay += 0.8;
    $gsap.fromTo(markersCanvas, fadeIn[0], fadeIn[1]);

    markersCanvas!
      .querySelectorAll('.marker')
      .forEach((marker: Element) =>
        $gsap.fromTo(marker, fadeInWithBlur[0], fadeInWithBlur[1]),
      );
  }

  activeSlide!
    .querySelectorAll('.title, .text, .button')
    .forEach((selector, i) => {
      delay += 0.4 * (i || 1);
      $gsap.fromTo(selector, slideUp[0], slideUp[1]).delay(delay);
    });
};

const runSlideEndAnimation = ($gsap: GSAP, rootEl: HTMLElement) => {
  const { fadeIn, slideUp } = { ...animations };
  fadeIn[0].duration = 0.1;
  slideUp[0].duration = 0.1;

  rootEl
    .querySelectorAll(
      '.swiper-slide-next .markers, .swiper-slide-prev .markers',
    )
    .forEach((marker: Element) => {
      $gsap.fromTo(marker, fadeIn[1], fadeIn[0]);
    });

  rootEl
    .querySelectorAll(
      '.swiper-slide-next .title, .swiper-slide-prev .title, .swiper-slide-prev .text,  .swiper-slide-next .text,  .swiper-slide-prev .button,  .swiper-slide-next .button',
    )
    .forEach((selector) => $gsap.fromTo(selector, slideUp[1], slideUp[0]));
};

const { $gsap } = useContext();
const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('lg');

const pagination = ref();

const runAnimation = (type: string) => {
  if (!swiperEl.value) return;

  (type === 'start' ? runSlideStartAnimation : runSlideEndAnimation)(
    $gsap,
    swiperEl.value,
  );
};

const swiperOptions = {
  speed: 1_000,
  pagination: {
    clickable: true,
    el: '.swiper-banners-pagination',
  },
  navigation: {
    prevEl: '.nav-prev',
    nextEl: '.nav-next',
    disabledClass: 'group-hover:opacity-50 group-hover:hover:opacity-75',
  },
  autoplay: {
    delay: 8_000,
    disableOnInteraction: false,
    waitForTransition: false,
  },
  loop: true,
  slidesPerView: 1,
  observer: true,
  on: {
    activeIndexChange: () => {
      pagination.value
        ?.querySelectorAll('.swiper-pagination-bullet')
        .forEach((el: HTMLElement) => {
          // eslint-disable-next-line no-param-reassign
          el.style.background = 'rgba(255, 255, 255, 0.4)';
        });
    },
    slideChangeTransitionStart: () => {
      hideAllPoppers();
    },
    slideChangeTransitionEnd: () => {
      runAnimation('end');
      runAnimation('start');
    },
  },
};

const swiperEl = useSwiperElement(swiperOptions);

// useSwiperAutoplayProgress из frontkit 0.4 написан под vue-awesome-swiper: каждый
// кадр читает `.value.$swiper`. Отдаём ему обёртку этой формы; frontkit 1.0 принимает
// сам swiperEl, тогда обёртка уйдёт. Геттер, а не computed: `swiper` появляется на
// элементе после initialize() и реактивности не даёт.
const swiperCompat = {
  get value() {
    const swiper = swiperEl.value?.swiper;
    return swiper ? { $swiper: swiper } : null;
  },
};
const { progress } = useSwiperAutoplayProgress(swiperCompat as any);
const isHovered = useElementHover(swiperEl as any);
const { pressed } = useMousePressed({ target: swiperEl as any });

const getCanvasImage = (src: string) =>
  getAssetUrl(src, isDesktop.value ? {} : { height: 600 });

const getBackgroundImage = (banner: any) => {
  return {
    backgroundImage: `url('${
      isDesktop.value
        ? getAssetUrl(banner.image)
        : getAssetUrl(banner.imageMobile)
    }')`,
  };
};

const swiperAutoplayToggle = (state: boolean) => {
  if (state) swiperEl.value?.swiper?.autoplay.stop();
  else swiperEl.value?.swiper?.autoplay.start();
};

watch(isHovered, (newValue) => {
  if (isTouchDevice()) return;
  swiperAutoplayToggle(newValue as any);
});

watch(pressed, (newValue) => {
  if (!isTouchDevice()) return;
  swiperAutoplayToggle(newValue as any);
});

watch(progress, (newValue) => {
  const activeBullet = pagination.value?.querySelector(
    '.swiper-pagination-bullet-active',
  );

  if (activeBullet)
    activeBullet.style.background = `linear-gradient(90deg, #00A7E1 ${newValue}%, rgba(255, 255, 255, 0.4) 0%)`;
});

onMounted(() => {
  runAnimation('start');
});

return { __sfc: true,getAssetUrl, duration, animations, runSlideStartAnimation, runSlideEndAnimation, $gsap, isDesktop, pagination, runAnimation, swiperOptions, swiperEl, swiperCompat, progress, isHovered, pressed, getCanvasImage, getBackgroundImage, swiperAutoplayToggle }
}

})